import { useRouter } from 'next/router';
import qs from 'qs';
import { useEffect } from 'react';
import SeoHead from '@/components/head/SeoHead';
import { CHAIN_ID } from '@/constants/constants';
import { BidStatus_Enum } from '@/utilities/__generated__/graphql';
import { budgetFormatter } from '@/utilities/budgetFormatter';
import { loadDeal } from '@/utilities/deals/loadDealById';
import { getNftThumbnail } from '@/utilities/getNftThumbnail';
import { getCollectionNetworkFromChainId } from '@/utilities/nftNetwork';
import { removeExtraSpaces } from '@/utilities/removeExtraSpaces';
import { getAppHost, toAbsoluteUrl } from '@/utilities/url';
import type { GetServerSideProps, NextPage } from 'next';

type Props = {
  ogImage: string;
  ogTitle: string;
  ogDescription: string;
  pagePath: string;
};

const DealLicensedPage: NextPage<Props> = ({
  ogImage,
  ogTitle,
  ogDescription,
  pagePath,
}: Props) => {
  const router = useRouter();
  useEffect(() => {
    router.push(`/deal/${router.query.id}`);
  }, [router]);

  return (
    <SeoHead
      title={ogTitle}
      imageURI={ogImage}
      description={ogDescription}
      pagePath={pagePath}
    />
  );
};

export default DealLicensedPage;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const id = Number(context.params?.id);
  const { opportunity } = await loadDeal({
    id,
    bidStatus: BidStatus_Enum.Executed,
    isAdmin: true,
  });

  if (!opportunity) return { notFound: true };

  const bidThreshold = 5; // The maximum amount of licensed assets we want to show on the OG image

  let nftTokenIds = context.query?.nftTokenIds;
  if (typeof nftTokenIds === 'string') {
    nftTokenIds = nftTokenIds.split(',').filter((id) => id); // Splitting by comma and filtering out any empty strings
  } else {
    nftTokenIds = [];
  }
  nftTokenIds = nftTokenIds.slice(0, bidThreshold);

  const bidAmount = opportunity.Bids.filter((bid) =>
    nftTokenIds?.includes(bid.token_id)
  ).reduce((acc, bid) => acc + Number(bid.Bids_private_info?.bid_amount), 0);

  const royaltyPercentage = opportunity.royalty_percentage;

  const formattedNftTokenIds = nftTokenIds.map((id) => `#${id}`).join(', ');
  const licensedAmount = `licensed NFTs ${formattedNftTokenIds}`;

  const ogDescription = removeExtraSpaces(
    `Exciting news! ${opportunity.Brand.company_name} ${licensedAmount} for ${
      opportunity.title
    } for ${budgetFormatter(
      bidAmount,
      undefined,
      opportunity.payment_currency
    ).trim()}. Read more about this deal, or submit your own proposal with OverpassIP.`
  );

  const opportunityBids = opportunity.Bids || [];
  const bids = opportunityBids.filter((bid) =>
    nftTokenIds?.includes(bid.token_id)
  );

  const assetImages = await Promise.all(
    nftTokenIds.map(async (tokenId: string) => {
      const matchingBid = opportunity.Bids.find(
        (bid) => bid.token_id === tokenId
      );

      if (!matchingBid || !matchingBid.Collection) {
        return null;
      }

      return getNftThumbnail({
        network: getCollectionNetworkFromChainId(
          matchingBid.Collection.nft_contract_address,
          CHAIN_ID
        ),
        nftContractAddress: matchingBid.Collection.nft_contract_address,
        tokenId,
      });
    })
  );

  const filteredAssetImages = assetImages.filter(
    (image: string | null): image is string => image !== null
  );

  const params = {
    nftImage: filteredAssetImages.join(','),
    totalBids: bids.length,
    bidAmount,
    currency: opportunity.payment_currency,
    royaltyPercentage,
  };

  const host = getAppHost();
  const ogImage = toAbsoluteUrl(
    `/api/og/licensed?${qs.stringify(params)}`,
    host
  );
  const ogTitle = removeExtraSpaces('Successfully Licensed!');
  return {
    props: {
      ogImage,
      ogTitle,
      ogDescription,
      pagePath: toAbsoluteUrl(context.resolvedUrl, host),
    },
  };
};
